import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Button, Input, Select } from '@/components/ui';
import type {
  FulfillmentStatus,
  FulfillmentType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  PickupStatus,
} from '@/lib/api-admin/types';
import {
  initialOrderFilterInputs,
  toIsoDate,
  useOrderFiltersStore,
  type OrderFilterInputs,
} from '@/stores/useOrderFiltersStore';
import { CheckboxGroupFilter } from './CheckboxGroupFilter';
import { ORDER_MALLS } from '../lib/orderMall';
import { SEARCHABLE_LABEL_CODES, searchLabelOf, type OrderLabelCode } from '../lib/displayLabelFilter';
import { splitOrderNoSearch } from '../lib/orderNoSearch';
import { ORDER_SEARCH_TYPES, type OrderSearchType } from '../lib/orderSearchType';

const parseAmount = (raw: string): number | null => {
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
};

// QA-625 상태값 정리 — 운영에서 쓰지 않는 값은 검색 선택지에서 뺀다(BE 정의·표시 이름은 그대로).
// 처리상태: 주문생성·상품준비중(보류)·부분취소·결제만료 제외.
const ORDER_STATUS_OPTIONS: ReadonlyArray<OrderStatus> = [
  'PENDING_PAYMENT',
  'PAYMENT_CONFIRMED',
  'READY_FOR_FULFILLMENT',
  'FULFILLING',
  'FULFILLED',
  'COMPLETED',
  'CANCEL_REQUESTED',
  'CANCELED',
  'FAILED',
];

// QA-625 결제상태: 승인대기·결제진행중·환불실패·기한만료 제외.
const PAYMENT_STATUS_OPTIONS: ReadonlyArray<PaymentStatus> = [
  'INITIATED',
  'CAPTURED',
  'FAILED',
  'CANCELED',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
];

const FULFILLMENT_TYPE_OPTIONS: ReadonlyArray<FulfillmentType> = ['DELIVERY', 'PICKUP'];

// QA-625 이행상태: 배송실패 제외.
const FULFILLMENT_STATUS_OPTIONS: ReadonlyArray<FulfillmentStatus> = [
  'PENDING',
  'READY',
  'FULFILLING',
  'FULFILLED',
];

// QA-625 픽업상태: 수령가능(픽업 기록에만 남고 주문은 준비대기 그대로)·수령진행중·픽업취소 제외.
const PICKUP_STATUS_OPTIONS: ReadonlyArray<PickupStatus> = [
  'WAITING_PREP',
  'COMPLETED',
  'NO_SHOW',
];

// QA-R6 M: 결제수단 필터에 '무통장입금'(BANK_TRANSFER) 누락 → 추가. PaymentMethod canonical
// enum 5값(CARD/VIRTUAL_ACCOUNT/KAKAOPAY/TOSSPAY/BANK_TRANSFER) 중 BANK_TRANSFER 만 빠져 있었다.
// VIRTUAL_ACCOUNT(가상계좌)와 BANK_TRANSFER(무통장입금)는 별개 결제수단이라 둘 다 노출한다.
// 값은 raw enum 그대로 paymentMethods 쿼리 파라미터로 전달(orderFilterParams.append).
const PAYMENT_METHOD_OPTIONS: ReadonlyArray<PaymentMethod> = [
  'CARD',
  'VIRTUAL_ACCOUNT',
  'BANK_TRANSFER',
  'KAKAOPAY',
  'TOSSPAY',
];

// QA-611 설계서 주문상태 34항목 — 미수령·픽업취소는 [픽업상태] 줄에서 고른다.
const ORDER_LABEL_OPTIONS: ReadonlyArray<OrderLabelCode> = SEARCHABLE_LABEL_CODES.filter(
  (c) => c !== 'PICKUP_NO_SHOW' && c !== 'PICKUP_CANCELED',
);

/** 주문일 빠른 기간 — 종료일 = 오늘, 시작일 자동 계산. 3개월이 기본(defaultOrderDateWindow 와 같은 월 단위). */
const PERIODS: ReadonlyArray<{ key: string; label: string; days?: number; months?: number }> = [
  { key: 'today', label: '오늘', days: 0 },
  { key: 'd7', label: '7일', days: 7 },
  { key: 'd15', label: '15일', days: 15 },
  { key: 'm1', label: '1개월', months: 1 },
  { key: 'm3', label: '3개월', months: 3 },
  { key: 'y1', label: '1년', months: 12 },
];

const periodFrom = (p: { days?: number; months?: number }, now: Date): string => {
  const d = new Date(now);
  if (p.months) d.setMonth(d.getMonth() - p.months);
  else d.setDate(d.getDate() - (p.days ?? 0));
  return toIsoDate(d);
};

const pickInputs = (s: OrderFilterInputs): OrderFilterInputs => ({
  searchType: s.searchType,
  q: s.q,
  ordererName: s.ordererName,
  ordererPhone: s.ordererPhone,
  malls: s.malls,
  paymentMethods: s.paymentMethods,
  displayLabelCodes: s.displayLabelCodes,
  orderStatuses: s.orderStatuses,
  paymentStatuses: s.paymentStatuses,
  fulfillmentTypes: s.fulfillmentTypes,
  fulfillmentStatuses: s.fulfillmentStatuses,
  pickupStatuses: s.pickupStatuses,
  dateFrom: s.dateFrom,
  dateTo: s.dateTo,
  amountMin: s.amountMin,
  amountMax: s.amountMax,
});

/**
 * 상세 검색을 닫으면 숨는 조건 중 기본값(전체·공란)과 다른 것의 개수 — [상세 검색 열기] 옆 뱃지.
 * 이행 유형·국가몰·주문상태·이행상태·픽업상태·처리상태·주문금액(최소·최대는 한 조건).
 */
export const hiddenChangedCount = (f: OrderFilterInputs): number =>
  [
    f.fulfillmentTypes.length > 0,
    f.malls.length > 0,
    f.displayLabelCodes.length > 0,
    f.fulfillmentStatuses.length > 0,
    f.pickupStatuses.length > 0,
    f.orderStatuses.length > 0,
    f.amountMin != null || f.amountMax != null,
  ].filter(Boolean).length;

/**
 * QA-611 주문 검색 바 — 현금영수증 관리 검색 영역과 같은 형태(라벨 96px | 입력, 체크박스 그룹, 하단 [검색][초기화]).
 * 고친 값은 여기 들고 있다가 [검색]·Enter 에서만 조회한다(체크박스를 여러 개 고른 뒤 한 번에).
 */
export const OrderFiltersBar = () => {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();
  const apply = useOrderFiltersStore((s) => s.apply);
  const [draft, setDraft] = useState<OrderFilterInputs>(() => pickInputs(useOrderFiltersStore.getState()));
  // QA-611 v1.1 — 처음 들어오면(다시 들어와도) 상세 검색은 닫힘.
  const [open, setOpen] = useState(false);
  const patch = (p: Partial<OrderFilterInputs>) => setDraft((prev) => ({ ...prev, ...p }));

  const isOrderNo = draft.searchType === 'ORDER_NO';
  const multiOrderNos = isOrderNo ? splitOrderNoSearch(draft.q) : { orderNos: null, overflow: false };

  const search = (next: OrderFilterInputs) => {
    apply(next);
    // 조건이 같아도 [검색]은 다시 조회한다(목록·합계).
    void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
  };

  const allLabel = t('orders.filters.all', { defaultValue: '전체' });
  const today = toIsoDate(new Date());

  const row = (label: ReactNode, children: ReactNode, testId?: string) => (
    <>
      <span className="py-1.5 font-semibold" style={{ color: 'var(--content-strong)' }}>
        {label}
      </span>
      <div className="min-w-0 md:col-span-3" data-testid={testId}>
        {children}
      </div>
    </>
  );

  const keywordInput = isOrderNo ? (
    // QA-511 주문번호는 쉼표·줄바꿈으로 여러 개(최대 10개) — 붙여넣은 줄바꿈이 살도록 여러 줄 입력. Enter = 검색, Shift+Enter = 줄바꿈.
    <textarea
      id="order-q"
      value={draft.q}
      onChange={(e) => patch({ q: e.target.value })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          search(draft);
        }
      }}
      placeholder={t('orders.filters.keywordPlaceholder.ORDER_NO', { defaultValue: '주문번호는 쉼표∙줄바꿈(Shift+Enter) 최대 10개 검색' })}
      aria-label={t('orders.filters.q')}
      rows={Math.min(4, Math.max(1, draft.q.split('\n').length))}
      className="block w-full resize-none rounded-md border px-3 py-[6px] text-[13px] leading-[18px] placeholder:text-[color:var(--content-soft)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
      style={{
        minHeight: 'var(--control-h-md)',
        background: 'var(--surface-0)',
        color: 'var(--content-strong)',
        borderColor: 'var(--divider-strong)',
      }}
      maxLength={400}
      data-testid="order-q"
    />
  ) : (
    <Input
      id="order-q"
      value={draft.q}
      onChange={(e) => patch({ q: e.target.value })}
      placeholder={t(`orders.filters.keywordPlaceholder.${draft.searchType}`)}
      aria-label={t('orders.filters.q')}
      className="w-full"
      maxLength={100}
      data-testid="order-q"
    />
  );

  const label = (text: ReactNode, htmlFor?: string) => (
    <span className="py-1.5 font-semibold" style={{ color: 'var(--content-strong)' }}>
      {htmlFor ? <label htmlFor={htmlFor}>{text}</label> : text}
    </span>
  );

  const keywordRow = (
    <>
      {label(t('orders.filters.q'), 'order-q')}
      <div className="min-w-0">
        <div className="flex gap-2">
          <Select
            className="w-36 shrink-0"
            value={draft.searchType}
            onChange={(e) => patch({ searchType: e.target.value as OrderSearchType })}
            aria-label={t('orders.filters.searchTypeLabel', { defaultValue: '검색어 유형' })}
            data-testid="order-search-type"
          >
            {ORDER_SEARCH_TYPES.map((st) => (
              <option key={st} value={st}>
                {t(`orders.filters.searchType.${st}`)}
              </option>
            ))}
          </Select>
          <div className="min-w-0 flex-1">{keywordInput}</div>
        </div>
        {multiOrderNos.orderNos ? (
          <p className="mt-1 text-[11px]" style={{ color: 'var(--content-muted)' }} data-testid="order-q-multi">
            {multiOrderNos.overflow
              ? t('orders.filters.multiOrderNoOverflow', {
                  count: multiOrderNos.orderNos.length,
                  defaultValue: '주문번호는 한 번에 {{count}}개까지 검색합니다. 뒤의 번호는 빠졌습니다.',
                })
              : t('orders.filters.multiOrderNo', {
                  count: multiOrderNos.orderNos.length,
                  defaultValue: '주문번호 {{count}}개를 검색합니다.',
                })}
          </p>
        ) : null}
      </div>
    </>
  );

  // 이행 유형 — 펼침에서만 검색어 오른쪽. 닫힘에서는 검색어 줄 오른쪽을 비운다.
  const fulfillmentTypeCell = open ? (
    <>
      {label(t('orders.filters.fulfillmentType'))}
      <CheckboxGroupFilter
        options={FULFILLMENT_TYPE_OPTIONS.map((v) => ({ value: v, label: t(`orders.fulfillmentType.${v}`) }))}
        value={draft.fulfillmentTypes}
        onChange={(v) => patch({ fulfillmentTypes: v })}
        allLabel={allLabel}
        aria-label={t('orders.filters.fulfillmentType')}
        data-testid="order-fulfillment-type"
      />
    </>
  ) : (
    <div className="hidden md:col-span-2 md:block" />
  );

  const ordererRow = (
    <>
      {label(t('orders.filters.ordererName'), 'order-orderer-name')}
      <Input
        id="order-orderer-name"
        value={draft.ordererName}
        onChange={(e) => patch({ ordererName: e.target.value })}
        placeholder={t('orders.filters.ordererNamePlaceholder')}
        className="w-full"
        maxLength={50}
      />
      {/* QA-624 — 연락처 또는 이메일. 파라미터는 ordererPhone 그대로, BE 가 둘 다 본다. */}
      {label(t('orders.filters.ordererPhone'), 'order-orderer-phone')}
      <Input
        id="order-orderer-phone"
        value={draft.ordererPhone}
        onChange={(e) => patch({ ordererPhone: e.target.value })}
        placeholder={t('orders.filters.ordererPhonePlaceholder')}
        className="w-full"
        maxLength={100}
      />
    </>
  );

  const mallRow = row(
    t('orders.filters.mall'),
    <CheckboxGroupFilter
      options={ORDER_MALLS.map((m) => ({ value: m, label: t(`orders.mall.${m}`) }))}
      value={draft.malls}
      onChange={(v) => patch({ malls: v })}
      allLabel={allLabel}
      aria-label={t('orders.filters.mall')}
      data-testid="order-mall"
    />,
  );
  const paymentMethodRow = row(
    t('orders.filters.paymentMethod'),
    <CheckboxGroupFilter
      options={PAYMENT_METHOD_OPTIONS.map((m) => ({ value: m, label: t(`orders.paymentMethod.${m}`) }))}
      value={draft.paymentMethods}
      onChange={(v) => patch({ paymentMethods: v })}
      allLabel={allLabel}
      aria-label={t('orders.filters.paymentMethod')}
      data-testid="order-payment-method"
    />,
  );
  // 주문상태 = 목록 상태 칸과 같은 노출이름(BE displayLabelCodes). 배송중을 고르면 배송중만 나온다.
  const orderStatusRow = row(
    t('orders.filters.orderStatus'),
    <CheckboxGroupFilter
      options={ORDER_LABEL_OPTIONS.map((c) => ({ value: c, label: searchLabelOf(c) }))}
      value={draft.displayLabelCodes}
      onChange={(v) => patch({ displayLabelCodes: v })}
      allLabel={allLabel}
      aria-label={t('orders.filters.orderStatus')}
      data-testid="order-status"
    />,
  );
  const fulfillmentStatusRow = row(
    t('orders.filters.fulfillmentStatus'),
    <CheckboxGroupFilter
      options={FULFILLMENT_STATUS_OPTIONS.map((s) => ({ value: s, label: t(`orders.fulfillmentStatus.${s}`) }))}
      value={draft.fulfillmentStatuses}
      onChange={(v) => patch({ fulfillmentStatuses: v })}
      allLabel={allLabel}
      aria-label={t('orders.filters.fulfillmentStatus')}
      data-testid="order-fulfillment-status"
    />,
  );
  const pickupStatusRow = row(
    t('orders.filters.pickupStatus'),
    <CheckboxGroupFilter
      options={PICKUP_STATUS_OPTIONS.map((s) => ({ value: s, label: t(`orders.pickupStatus.${s}`) }))}
      value={draft.pickupStatuses}
      onChange={(v) => patch({ pickupStatuses: v })}
      allLabel={allLabel}
      aria-label={t('orders.filters.pickupStatus')}
      data-testid="order-pickup-status"
    />,
  );
  const orderDateRow = row(
    t('orders.filters.orderDate', { defaultValue: '주문일' }),
    <div className="flex flex-wrap items-center gap-2">
      <Input
        id="order-date-from"
        type="date"
        value={draft.dateFrom ?? ''}
        onChange={(e) => patch({ dateFrom: e.target.value === '' ? null : e.target.value })}
        aria-label={t('orders.filters.dateFrom')}
        className="w-40"
      />
      <span style={{ color: 'var(--content-muted)' }}>~</span>
      <Input
        id="order-date-to"
        type="date"
        value={draft.dateTo ?? ''}
        onChange={(e) => patch({ dateTo: e.target.value === '' ? null : e.target.value })}
        aria-label={t('orders.filters.dateTo')}
        className="w-40"
      />
      {/* 빠른 기간 — 고른 버튼은 채워서 보인다. 날짜를 직접 바꾸면 맞는 버튼이 없어 선택이 풀린다. */}
      <div className="inline-flex flex-wrap items-center gap-1" data-testid="order-date-periods">
        {PERIODS.map((p) => {
          const from = periodFrom(p, new Date());
          const active = draft.dateTo === today && draft.dateFrom === from;
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={active}
              onClick={() => patch({ dateFrom: from, dateTo: today })}
              className="rounded-md px-2.5 py-1 text-[12px]"
              style={{
                background: active ? 'var(--content-strong)' : 'transparent',
                color: active ? 'var(--surface-0)' : 'var(--content-muted)',
                fontWeight: active ? 700 : 400,
              }}
              data-testid={`order-date-period-${p.key}`}
            >
              {t(`orders.filters.period.${p.key}`, { defaultValue: p.label })}
            </button>
          );
        })}
      </div>
    </div>,
  );
  const amountRow = row(
    t('orders.filters.amountRange'),
    <div className="flex items-center gap-2">
      <Input
        id="order-amount-min"
        type="number"
        inputMode="numeric"
        min={0}
        value={draft.amountMin ?? ''}
        onChange={(e) => patch({ amountMin: parseAmount(e.target.value) })}
        placeholder={t('orders.filters.amountMin')}
        aria-label={t('orders.filters.amountMin')}
        className="w-40"
      />
      <span style={{ color: 'var(--content-muted)' }}>~</span>
      <Input
        id="order-amount-max"
        type="number"
        inputMode="numeric"
        min={0}
        value={draft.amountMax ?? ''}
        onChange={(e) => patch({ amountMax: parseAmount(e.target.value) })}
        placeholder={t('orders.filters.amountMax')}
        aria-label={t('orders.filters.amountMax')}
        className="w-40"
      />
    </div>,
  );
  const orderStatusCanonicalRow = row(
    t('orders.filters.orderStatusCanonical', { defaultValue: '처리상태' }),
    <CheckboxGroupFilter
      options={ORDER_STATUS_OPTIONS.map((s) => ({ value: s, label: t(`orders.orderStatus.${s}`) }))}
      value={draft.orderStatuses}
      onChange={(v) => patch({ orderStatuses: v })}
      allLabel={allLabel}
      aria-label={t('orders.filters.orderStatusCanonical', { defaultValue: '처리상태' })}
      data-testid="order-status-canonical"
    />,
  );
  const paymentStatusRow = row(
    t('orders.filters.paymentStatus'),
    <CheckboxGroupFilter
      options={PAYMENT_STATUS_OPTIONS.map((s) => ({ value: s, label: t(`orders.paymentStatus.${s}`) }))}
      value={draft.paymentStatuses}
      onChange={(v) => patch({ paymentStatuses: v })}
      allLabel={allLabel}
      aria-label={t('orders.filters.paymentStatus')}
      data-testid="order-payment-status"
    />,
  );

  const hiddenCount = hiddenChangedCount(draft);

  return (
    <form
      aria-label={t('orders.filters.title')}
      className="flex flex-col gap-3 rounded-lg p-4 text-[13px]"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      onSubmit={(e) => {
        e.preventDefault();
        search(draft);
      }}
      data-testid="orders-filters"
    >
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-expanded={open}
          aria-controls="order-advanced-filters"
          onClick={() => setOpen((v) => !v)}
          data-testid="order-advanced-toggle"
        >
          {open
            ? t('orders.filters.advancedHide', { defaultValue: '상세 검색 닫기' })
            : t('orders.filters.advancedShow', { defaultValue: '상세 검색 열기' })}
          {open ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
          {!open && hiddenCount > 0 ? (
            <span
              className="ml-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-bold"
              style={{ background: 'var(--accent-default)', color: 'var(--cta-primary-on)' }}
              aria-label={t('orders.filters.hiddenChanged', {
                count: hiddenCount,
                defaultValue: '숨긴 조건 {{count}}개 적용 중',
              })}
              data-testid="order-advanced-badge"
            >
              {hiddenCount}
            </span>
          ) : null}
        </Button>
      </div>

      {/* QA-611 v1.1 — 닫힘(기본): 검색어·주문자명·연락처·결제수단·주문일·결제상태. 숨긴 조건도 값은 그대로 조회에 쓴다. */}
      <div
        id="order-advanced-filters"
        className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-2 md:grid-cols-[96px_minmax(0,1fr)_96px_minmax(0,1fr)]"
      >
        {keywordRow}
        {fulfillmentTypeCell}
        {ordererRow}
        {open ? (
          <>
            {mallRow}
            {paymentMethodRow}
            {orderStatusRow}
            {fulfillmentStatusRow}
            {pickupStatusRow}
            {orderDateRow}
            {amountRow}
            {/* 처리상태·결제상태 — canonical 축. 검색 바 가장 아래(주문금액 다음). */}
            {orderStatusCanonicalRow}
            {paymentStatusRow}
          </>
        ) : (
          <>
            {paymentMethodRow}
            {orderDateRow}
            {paymentStatusRow}
          </>
        )}
      </div>

      <div className="flex justify-center gap-2">
        <Button type="submit" size="sm" variant="primary" data-testid="order-search-submit">
          {t('orders.actions.search', { defaultValue: '검색' })}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            // 숨긴 조건까지 기본값으로 되돌려 조회한다(열림·닫힘 상태는 그대로).
            const init = initialOrderFilterInputs();
            setDraft(init);
            search(init);
          }}
          data-testid="order-search-reset"
        >
          {t('orders.actions.reset', { defaultValue: '초기화' })}
        </Button>
      </div>
    </form>
  );
};
